import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabaseClient";
import {
  Check,
  X,
  Mail,
  Shield,
  User,
  MapPin,
  Trophy,
  AlignLeft,
  Tag,
  Globe,
  Instagram,
  Search,
  Loader2,
} from "lucide-react";
import ImageUpload from "../../components/ImageUpload";

const AGE_OPTIONS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];

const InputCard = ({
  icon: Icon,
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  error,
  disabled,
}) => (
  <div>
    <div
      className={`bg-white rounded-xl p-3 flex items-center gap-3 border shadow-sm transition-all focus-within:ring-2 focus-within:ring-blue-100 ${
        error ? "border-red-300 ring-2 ring-red-50" : "border-gray-100"
      } ${disabled ? "bg-gray-50" : ""}`}
    >
      <div
        className={`p-2 rounded-lg ${
          error ? "bg-red-50 text-red-400" : "bg-gray-50 text-gray-400"
        }`}
      >
        <Icon size={18} />
      </div>
      <div className="flex-1">
        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">
          {label}
        </label>
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full text-gray-900 font-bold text-sm bg-transparent border-none p-0 focus:ring-0 placeholder-gray-300 ${
            disabled ? "text-gray-500" : ""
          }`}
        />
      </div>
    </div>
    {error && <p className="text-xs text-red-500 mt-1 pl-1 font-medium">{error}</p>}
  </div>
);

const SelectCard = ({ icon: Icon, label, value, onChange, options, error, disabled }) => (
  <div>
    <div
      className={`bg-white rounded-xl p-3 flex items-center gap-3 border shadow-sm transition-all focus-within:ring-2 focus-within:ring-blue-100 ${
        error ? "border-red-300 ring-2 ring-red-50" : "border-gray-100"
      } ${disabled ? "bg-gray-50" : ""}`}
    >
      <div
        className={`p-2 rounded-lg ${
          error ? "bg-red-50 text-red-400" : "bg-gray-50 text-gray-400"
        }`}
      >
        <Icon size={18} />
      </div>
      <div className="flex-1 relative">
        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">
          {label}
        </label>
        <select
          value={value}
          onChange={onChange}
          disabled={disabled}
          className={`w-full text-gray-900 font-bold text-sm bg-transparent border-none p-0 focus:ring-0 appearance-none cursor-pointer ${
            disabled ? "text-gray-500" : ""
          }`}
        >
          <option value="">Select...</option>
          {options.map((opt) => (
            <option key={opt.name || opt} value={opt.name || opt}>
              {opt.name || opt}
            </option>
          ))}
        </select>
      </div>
    </div>
    {error && <p className="text-xs text-red-500 mt-1 pl-1 font-medium">{error}</p>}
  </div>
);

const AgeRangeSelector = ({ label, minVal, maxVal, onMinChange, onMaxChange, error, disabled }) => (
  <div className={`bg-gray-50 p-4 rounded-xl border ${error ? "border-red-200" : "border-gray-200"}`}>
    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{label}</p>
    <div className="flex items-center gap-2">
      <select
        value={minVal || ""}
        onChange={(e) => onMinChange(Number(e.target.value))}
        disabled={disabled}
        className="flex-1 bg-white border-gray-300 rounded-lg text-sm font-bold py-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="">Min Age</option>
        {AGE_OPTIONS.map((age) => (
          <option key={age} value={age}>
            {age}
          </option>
        ))}
      </select>
      <span className="text-gray-400 font-bold">-</span>
      <select
        value={maxVal || ""}
        onChange={(e) => onMaxChange(Number(e.target.value))}
        disabled={disabled}
        className="flex-1 bg-white border-gray-300 rounded-lg text-sm font-bold py-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="">Max Age</option>
        {AGE_OPTIONS.map((age) => (
          <option key={age} value={age} disabled={minVal && age < minVal}>
            {age}
          </option>
        ))}
      </select>
    </div>
    {error && <p className="text-xs text-red-500 mt-2 font-bold">{error}</p>}
  </div>
);

export default function SkillsTrainerProfile({ userId }) {
  const [profile, setProfile] = useState(null);
  const [invites, setInvites] = useState([]);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processingInvite, setProcessingInvite] = useState(null);

  const [program, setProgram] = useState(null);
  const [sports, setSports] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const [addressLoading, setAddressLoading] = useState(false);
  const [programSaving, setProgramSaving] = useState(false);
  const [programError, setProgramError] = useState("");
  const [activeTab, setActiveTab] = useState("program");

  const [form, setForm] = useState({
    trainer_name: "",
    sport: "",
    bio: "",
    skills_taught_ids: [],
    trainer_logo: null,
    location_address: "",
    latitude: null,
    longitude: null,
    ages_min: "",
    ages_max: "",
    website_url: "",
    ig_handle: "",
  });

  useEffect(() => {
    async function fetchProfile() {
      if (!userId) return;
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("id", userId)
        .single();
      if (error) console.error("Error fetching profile:", error);
      else setProfile(data);
    }
    fetchProfile();
  }, [userId]);

  useEffect(() => {
    async function fetchProgram() {
      if (!userId) return;
        const { data, error } = await supabase
        .from("programs")
        .select(
          "id, name, description, sport, age_groups, location_address, latitude, longitude, program_logo, website_url, ig_handle, program_type"
        )
        .eq("administrator", userId)
        .eq("program_type", "Skills Trainer")
        .maybeSingle();

      if (error) {
        console.error("Error fetching program:", error);
        return;
      }

      if (data) {
        setProgram(data);
        const ageNums = (data.age_groups || []).map((v) => Number(v)).filter((v) => !Number.isNaN(v));
        const minAge = ageNums.length ? Math.min(...ageNums) : "";
        const maxAge = ageNums.length ? Math.max(...ageNums) : "";
        setForm((prev) => ({
          ...prev,
          trainer_name: data.name || "",
          sport: data.sport || "",
          bio: data.description || "",
          trainer_logo: data.program_logo || null,
          location_address: data.location_address || "",
          latitude: data.latitude || null,
          longitude: data.longitude || null,
          ages_min: minAge,
          ages_max: maxAge,
          website_url: data.website_url || "",
          ig_handle: data.ig_handle || "",
        }));
      }
    }

    fetchProgram();
  }, [userId]);

  useEffect(() => {
    async function fetchSports() {
      const { data } = await supabase.from("development_sports").select("name");
      if (data) setSports(data);
    }
    fetchSports();
  }, []);

  useEffect(() => {
    const fetchSkills = async () => {
      if (!form.sport) {
        setSections([]);
        setSelectedSectionId("");
        return;
      }

      const { data: sportData } = await supabase
        .from("development_sports")
        .select("id")
        .eq("name", form.sport)
        .single();

      if (!sportData?.id) return;

      const { data: sectionsData } = await supabase
        .from("development_sections")
        .select("id, name")
        .eq("sport_id", sportData.id)
        .order("name");

      setSections(sectionsData || []);
      setSelectedSectionId("");
    };

    fetchSkills();
  }, [form.sport]);

  useEffect(() => {
    const fetchSkillsTaught = async () => {
      if (!program?.id) return;
      const { data, error } = await supabase
        .from("trainer_skill_categories")
        .select("section_id")
        .eq("program_id", program.id);
      if (error) {
        console.error("Error fetching trainer skills:", error);
        return;
      }
      const ids = (data || []).map((row) => String(row.section_id));
      setForm((prev) => ({ ...prev, skills_taught_ids: ids }));
    };

    fetchSkillsTaught();
  }, [program?.id]);

  useEffect(() => {
    if (!profile?.email) return;

    async function fetchInvites() {
      const { data, error } = await supabase
        .from("coach_invites")
        .select(
          `
          *,
          programs (name, sport),
          teams (name)
        `
        )
        .eq("email", profile.email)
        .eq("status", "pending");

      if (error) console.error("Error fetching invites:", error);
      else setInvites(data || []);
    }
    fetchInvites();
  }, [profile?.email]);

  const handleInviteResponse = async (invite, action) => {
    setProcessingInvite(invite.id);
    try {
      const newStatus = action === "accept" ? "accepted" : "rejected";
      const { error: updateError } = await supabase
        .from("coach_invites")
        .update({ status: newStatus })
        .eq("id", invite.id);

      if (updateError) throw updateError;

      if (action === "accept") {
        const { error: insertError } = await supabase.from("program_coaches").insert([
          {
            program_id: invite.program_id,
            team_id: invite.team_id || null,
            user_id: userId,
            role: invite.role || "SkillsTrainer",
            status: "active",
          },
        ]);
        if (insertError) throw insertError;
      }

      setInvites((prev) => prev.filter((i) => i.id !== invite.id));
    } catch (err) {
      console.error("Error processing invite:", err);
      alert("Error processing invite. Please try again.");
    } finally {
      setProcessingInvite(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleProgramChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const addSkill = () => {
    if (!selectedSectionId) return;
    const idValue = String(selectedSectionId);
    if (!idValue) return;
    if (form.skills_taught_ids.includes(idValue)) return;
    setForm((prev) => ({
      ...prev,
      skills_taught_ids: [...prev.skills_taught_ids, idValue],
    }));
  };

  const removeSkill = (skillId) => {
    setForm((prev) => ({
      ...prev,
      skills_taught_ids: prev.skills_taught_ids.filter((s) => s !== skillId),
    }));
  };

  const handleAddressSearch = async () => {
    if (!form.location_address) return;
    setAddressLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          form.location_address
        )}`
      );
      const data = await response.json();
      if (data?.[0]) {
        setForm((prev) => ({
          ...prev,
          location_address: data[0].display_name,
          latitude: parseFloat(data[0].lat),
          longitude: parseFloat(data[0].lon),
        }));
      }
    } catch (err) {
      console.error("Geocoding failed", err);
    } finally {
      setAddressLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setProgramSaving(true);
    setProgramError("");

    try {
      const { error: userError } = await supabase
        .from("users")
        .update({
          full_name: profile.full_name,
          email: profile.email,
          phone_number: profile.phone_number,
          user_school: profile.user_school,
          bio: profile.bio,
        })
        .eq("id", userId);

      if (userError) throw userError;

      if (program?.id) {
        const ages = form.ages_min && form.ages_max
          ? Array.from(
              { length: Number(form.ages_max) - Number(form.ages_min) + 1 },
              (_, i) => String(Number(form.ages_min) + i)
            )
          : [];

        const locationPoint =
          form.latitude && form.longitude
            ? `POINT(${form.longitude} ${form.latitude})`
            : null;

        const { error: programError } = await supabase
          .from("programs")
          .update({
            name: form.trainer_name,
            description: form.bio,
            sport: form.sport,
            age_groups: ages,
            location_address: form.location_address,
            latitude: form.latitude,
            longitude: form.longitude,
            location: locationPoint,
            program_logo: form.trainer_logo,
            website_url: form.website_url,
            ig_handle: form.ig_handle,
          })
          .eq("id", program.id);

        if (programError) throw programError;

        const { error: deleteError } = await supabase
          .from("trainer_skill_categories")
          .delete()
          .eq("program_id", program.id);

        if (deleteError) throw deleteError;

        if (form.skills_taught_ids.length > 0) {
          const skillRows = form.skills_taught_ids.map((sectionId) => ({
            program_id: program.id,
            section_id: sectionId,
          }));

          const { error: insertError } = await supabase
            .from("trainer_skill_categories")
            .insert(skillRows);

          if (insertError) throw insertError;
        }
      }

      setEditing(false);
    } catch (err) {
      console.error("Error updating profile:", err);
      setProgramError(err.message || "Unable to save changes.");
    } finally {
      setLoading(false);
      setProgramSaving(false);
    }
  };

  const skillLabels = useMemo(() => {
    const map = new Map();
    sections.forEach((s) => map.set(String(s.id), s.name));
    return map;
  }, [sections]);

  if (!profile) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 pb-24 space-y-5">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col sm:flex-row items-center gap-6">
        <div className="relative">
          <img
            src={profile.profile_img || "https://via.placeholder.com/150"}
            alt="Profile"
            className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover border-4 border-gray-50 shadow-md"
          />
          <div className="absolute bottom-0 right-0 bg-blue-600 p-1.5 rounded-full border-2 border-white text-white">
            <User size={16} />
          </div>
        </div>
        <div className="text-center sm:text-left flex-1">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
            {profile.full_name || "Unnamed User"}
          </h1>
          <p className="text-gray-500 font-medium">{profile.role || "SkillsTrainer"}</p>
          <p className="text-gray-400 text-sm mt-1">{profile.email}</p>
          <button
            onClick={() => setEditing(!editing)}
            className="mt-4 px-6 py-2 bg-gray-900 text-white rounded-xl font-bold hover:bg-black transition-all shadow-lg shadow-gray-200 active:scale-95"
          >
            {editing ? "Cancel Edit" : "Edit Profile"}
          </button>
        </div>
      </div>

      {invites.length > 0 && (
        <div className="animate-in slide-in-from-top-4 duration-500">
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="bg-blue-100 p-2 rounded-lg text-blue-600">
                <Mail size={20} />
              </div>
              <h2 className="text-lg font-bold text-blue-900">Pending Invitations</h2>
            </div>

            <div className="grid gap-3">
              {invites.map((invite) => (
                <div
                  key={invite.id}
                  className="bg-white p-4 rounded-xl shadow-sm border border-blue-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
                >
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-gray-900 text-lg">
                        {invite.programs?.name}
                      </h3>
                      {invite.role && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 px-2 py-0.5 rounded-md">
                          {invite.role}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      {invite.teams?.name ? (
                        <>
                          Assigning to Team:{" "}
                          <span className="font-semibold text-gray-700">
                            {invite.teams.name}
                          </span>
                        </>
                      ) : (
                        <>
                          <Shield size={14} /> Program Wide Access
                        </>
                      )}
                    </p>
                  </div>

                  <div className="flex w-full sm:w-auto gap-2">
                    <button
                      onClick={() => handleInviteResponse(invite, "accept")}
                      disabled={processingInvite === invite.id}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-green-700 transition active:scale-95 disabled:opacity-50"
                    >
                      <Check size={18} /> Accept
                    </button>
                    <button
                      onClick={() => handleInviteResponse(invite, "decline")}
                      disabled={processingInvite === invite.id}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white border border-gray-200 text-gray-600 px-4 py-2 rounded-lg font-bold hover:bg-gray-50 transition active:scale-95 disabled:opacity-50"
                    >
                      <X size={18} /> Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow-sm border border-gray-100 rounded-2xl p-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {[
            { id: "program", label: "Program" },
            { id: "skills", label: "Skills & Ages" },
            { id: "location", label: "Location" },
            { id: "media", label: "Links & Media" },
            { id: "personal", label: "Personal" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${
                activeTab === tab.id
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
          {!program && (
            <span className="ml-auto text-[11px] font-semibold text-amber-600">
              Create a trainer program to enable edits.
            </span>
          )}
        </div>

        {programError && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl flex items-center gap-2">
            <span className="text-sm font-bold">{programError}</span>
          </div>
        )}

        {activeTab === "program" && (
          <div className="grid gap-4">
            <InputCard
              icon={User}
              label="Trainer Name"
              placeholder="Your name or brand"
              value={form.trainer_name}
              onChange={(e) => handleProgramChange("trainer_name", e.target.value)}
              disabled={!editing || !program}
            />
            <SelectCard
              icon={Trophy}
              label="Primary Sport"
              value={form.sport}
              onChange={(e) => handleProgramChange("sport", e.target.value)}
              options={sports}
              disabled={!editing || !program}
            />
            <div
              className={`bg-white rounded-xl p-3 border border-gray-100 flex gap-3 ${
                !editing || !program ? "bg-gray-50" : ""
              }`}
            >
              <div className="bg-gray-50 p-2 rounded-lg text-gray-400 h-fit">
                <AlignLeft size={18} />
              </div>
              <div className="flex-1">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
                  Background
                </label>
                <textarea
                  rows={4}
                  placeholder="Share your coaching background and approach..."
                  value={form.bio}
                  onChange={(e) => handleProgramChange("bio", e.target.value)}
                  disabled={!editing || !program}
                  className={`w-full text-gray-900 font-medium text-sm border-none p-0 focus:ring-0 resize-none ${
                    !editing || !program ? "bg-gray-50 text-gray-500" : ""
                  }`}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === "skills" && (
          <div className="space-y-4">
            <div className="bg-white p-4 rounded-2xl border border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <Tag className="text-blue-500" size={18} />
                <h4 className="text-sm font-bold text-gray-900">Skills You Teach Best</h4>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
                    Category
                  </label>
                  <select
                    value={selectedSectionId}
                    onChange={(e) => setSelectedSectionId(e.target.value)}
                    disabled={!editing || !program}
                    className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl py-3 px-3 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-medium"
                  >
                    <option value="">Select category</option>
                    {sections.map((section) => (
                      <option key={section.id} value={section.id}>
                        {section.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                type="button"
                onClick={addSkill}
                className="mt-3 px-4 py-2 rounded-xl text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50"
                disabled={!selectedSectionId || !editing || !program}
              >
                Add Category
              </button>

              <div className="mt-4 space-y-2">
                {form.skills_taught_ids.map((skillId) => (
                  <div
                    key={skillId}
                    className="flex items-center justify-between bg-blue-50 border border-blue-200 text-blue-700 px-3 py-2 rounded-xl text-sm font-bold"
                  >
                    <span>{skillLabels.get(String(skillId)) || "Unknown category"}</span>
                    <button
                      type="button"
                      onClick={() => removeSkill(skillId)}
                      disabled={!editing || !program}
                      className="text-xs font-bold text-blue-700 hover:text-blue-900 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <AgeRangeSelector
              label="Ages You Work With"
              minVal={form.ages_min}
              maxVal={form.ages_max}
              onMinChange={(v) => handleProgramChange("ages_min", v)}
              onMaxChange={(v) => handleProgramChange("ages_max", v)}
              disabled={!editing || !program}
            />
          </div>
        )}

        {activeTab === "location" && (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
              <InputCard
                icon={MapPin}
                label="Facility Address"
                placeholder="e.g. 123 Main St, Seattle, WA"
                value={form.location_address}
                onChange={(e) => handleProgramChange("location_address", e.target.value)}
                disabled={!editing || !program}
              />
              <button
                onClick={handleAddressSearch}
                disabled={addressLoading || !editing || !program}
                className="h-[50px] px-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 flex items-center justify-center transition-colors shadow-sm disabled:opacity-50"
              >
                {addressLoading ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <Search size={20} />
                )}
              </button>
            </div>
          </div>
        )}

        {activeTab === "media" && (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <InputCard
                icon={Globe}
                label="Website"
                placeholder="www.yoursite.com"
                value={form.website_url}
                onChange={(e) => handleProgramChange("website_url", e.target.value)}
                disabled={!editing || !program}
              />
              <InputCard
                icon={Instagram}
                label="Instagram"
                placeholder="@handle"
                value={form.ig_handle}
                onChange={(e) => handleProgramChange("ig_handle", e.target.value)}
                disabled={!editing || !program}
              />
            </div>
            <div className="bg-white p-4 rounded-2xl border border-gray-100">
              <h4 className="text-sm font-bold text-gray-900 mb-3">Profile Image</h4>
              <ImageUpload
                bucket="program_logos"
                currentImage={form.trainer_logo}
                onUploadComplete={(result) =>
                  setForm((prev) => ({ ...prev, trainer_logo: result.fullUrl }))
                }
                height="h-20"
                width="w-20"
                shape="rounded-full"
              />
            </div>
          </div>
        )}

        {activeTab === "personal" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
            {[
              { label: "Full Name", name: "full_name" },
              { label: "Email", name: "email" },
              { label: "Phone Number", name: "phone_number" },
              { label: "School/Organization", name: "user_school" },
            ].map((field) => (
              <div key={field.name} className="space-y-1">
                <label className="text-xs font-semibold text-gray-500">{field.label}</label>
                <input
                  name={field.name}
                  value={profile[field.name] || ""}
                  onChange={handleChange}
                  disabled={!editing}
                  className={`w-full rounded-xl px-4 py-3 text-sm border ${
                    editing
                      ? "border-gray-200 focus:ring-2 focus:ring-blue-500"
                      : "border-transparent bg-gray-50 text-gray-500"
                  }`}
                />
              </div>
            ))}

            <div className="md:col-span-2 space-y-1">
              <label className="text-xs font-semibold text-gray-500">Bio</label>
              <textarea
                name="bio"
                value={profile.bio || ""}
                onChange={handleChange}
                disabled={!editing}
                rows={4}
                className={`w-full rounded-xl px-4 py-3 text-sm border ${
                  editing
                    ? "border-gray-200 focus:ring-2 focus:ring-blue-500"
                    : "border-transparent bg-gray-50 text-gray-500"
                }`}
              />
            </div>
          </div>
        )}
      </div>

      {editing && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={loading || programSaving}
            className="px-6 py-2 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition disabled:opacity-50"
          >
            {programSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      )}
    </div>
  );
}
